import React, { useState } from 'react';
import axios from 'axios';
import './superguaranteecalculator.css';
import { API_BASE_URL, NGROK_URL } from '../../utils/const';

const SuperGuaranteeContributionsCalculator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [savedEmployees, setSavedEmployees] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [calculationResults, setCalculationResults] = useState(null);

  const initialEmployeeState = {
    name: '',
    fund: '',
    earnings: '',
    rate: '11.5'
  };

  const handleAddEmployee = () => {
    setCurrentEmployee(initialEmployeeState);
    setEditingIndex(null);
  };

  const handleEmployeeChange = (field, value) => {
    setCurrentEmployee(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!currentEmployee) return;

    const employeeData = {
      ...currentEmployee,
      contribution: ((parseFloat(currentEmployee.earnings) * parseFloat(currentEmployee.rate)) / 100).toFixed(2)
    };

    if (editingIndex !== null) {
      // Update existing employee
      const updatedEmployees = [...savedEmployees];
      updatedEmployees[editingIndex] = employeeData;
      setSavedEmployees(updatedEmployees);
    } else {
      // Add new employee
      setSavedEmployees(prev => [...prev, employeeData]);
    }

    setCurrentEmployee(null);
    setEditingIndex(null);
  };

  const handleEdit = (index) => {
    setCurrentEmployee(savedEmployees[index]);
    setEditingIndex(index);
    setExpandedIndex(null);
  };

  const handleDelete = (index) => {
    setSavedEmployees(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setCurrentEmployee(null);
    setEditingIndex(null);
  };

  const handleCalculate = async () => {
    setIsLoading(true);
    try {
      const payload = {
        employees: savedEmployees.map(emp => ({
          name: emp.name,
          earnings: parseFloat(emp.earnings),
          rate: parseFloat(emp.rate),
          fund: emp.fund
        }))
      };

      const response = await axios.post(API_BASE_URL+'/ato_calculator/super_guarantee_contributions_calculator', payload);
      setCalculationResults(response.data.sgc_results_by_fund);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderResults = () => {
    if (!calculationResults) return null;

    return (
      <div className="results-container">
        <h2 className="results-title">Super guarantee contributions report</h2>
        
        {Object.entries(calculationResults).map(([fund, data]) => (
          <React.Fragment key={fund}>
            <div className="fund-header">
              {fund}
            </div>

            <table className="results-table">
              <thead>
                <tr>
                  <th>Employee's name</th>
                  <th>Ordinary time earnings</th>
                  <th>SG rate</th>
                  <th>SG contribution</th>
                </tr>
              </thead>
              <tbody>
                {data.employees.map((emp, index) => (
                  <tr key={index}>
                    <td>{emp.name}</td>
                    <td>{emp.earnings}</td>
                    <td>{emp.rate}</td>
                    <td>{emp.contribution}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="3">Total contributions payable to {fund}</td>
                  <td>{data.total_contribution}</td>
                </tr>
              </tbody>
            </table>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="calculator-container">
      <div className="calculator-header">
        <h1>Super guarantee contributions calculator</h1>
        <button className="help-button">Help ?</button>
      </div>
      
      <p className="calculator-description">
        Use this tool to work out how much super you need to contribute for your eligible employees.
      </p>
      
      <div className="employees-header">
        <h2>Employees</h2>
        <button 
          className="add-button"
          onClick={handleAddEmployee}
          disabled={currentEmployee !== null}
        >
          Add
        </button>
      </div>

      {/* Employee Form */}
      {currentEmployee && (
        <form onSubmit={handleSave} className="employee-form">
          <div className="form-group">
            <label>Employee name *</label>
            <input
              type="text"
              value={currentEmployee.name}
              onChange={(e) => handleEmployeeChange('name', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Super fund name *</label>
            <input
              type="text"
              value={currentEmployee.fund}
              onChange={(e) => handleEmployeeChange('fund', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Ordinary time earnings *</label>
            <div className="input-group">
              <span>$</span>
              <input
                type="number"
                value={currentEmployee.earnings}
                onChange={(e) => handleEmployeeChange('earnings', e.target.value)}
                required
              />
              <span>.00</span>
            </div>
          </div>

          <div className="form-group">
            <label>SG rate *</label>
            <div className="input-group">
              <input
                type="number"
                value={currentEmployee.rate}
                onChange={(e) => handleEmployeeChange('rate', e.target.value)}
                step="0.1"
                required
              />
              <span>%</span>
            </div>
          </div>

          <div className="button-group">
            <button type="button" onClick={handleCancel}>Cancel</button>
            <button type="submit">Save</button>
          </div>
        </form>
      )}

      {/* Saved Employees List */}
      {savedEmployees.length > 0 && (
        <div className="saved-employees">
          <table>
            <thead>
              <tr>
                <th>Employee's name</th>
                <th>SG contribution</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {savedEmployees.map((emp, index) => (
                <React.Fragment key={index}>
                  <tr className="employee-row">
                    <td>{emp.name}</td>
                    <td>{emp.contribution}</td>
                    <td>
                      <button 
                        className="expand-button"
                        onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                      >
                        {expandedIndex === index ? '▼' : '▶'}
                      </button>
                    </td>
                  </tr>
                  {expandedIndex === index && (
                    <tr className="details-row">
                      <td colSpan="3">
                        <div className="employee-details">
                          <p><strong>Super fund:</strong> {emp.fund}</p>
                          <p><strong>Earnings:</strong> {emp.earnings}</p>
                          <p><strong>Rate:</strong> {emp.rate}%</p>
                          <div className="actions">
                            <button onClick={() => handleEdit(index)}>Edit</button>
                            <button onClick={() => handleDelete(index)}>Delete</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {savedEmployees.length > 0 && (
        <button 
          className="calculate-button"
          onClick={handleCalculate}
          disabled={isLoading}
        >
          {isLoading ? 'Calculating...' : 'Show result'}
        </button>
      )}

      {!isLoading && calculationResults && (
        <div className="results-section">
          {renderResults()}
        </div>
      )}
    </div>
  );
};

export default SuperGuaranteeContributionsCalculator;